import { useDisclosure } from '@mantine/hooks';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button, ConfirmActionModal } from '../../../../../components/Atoms';
import { useDestroyInstanceMutate } from '../../../../../hooks/CloudComputeHooks/useDestoryInstanceMutate';

interface DangerZoneCardProps {
    instanceId: number;
    hostname: string;
}

export default function DangerZoneCard({ instanceId, hostname }: DangerZoneCardProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const navigate = useNavigate();
    const destroyMutation = useDestroyInstanceMutate();
    const [opened, { open, close }] = useDisclosure();

    const confirmDestroy = () => {
        destroyMutation.mutate(instanceId, {
            onSuccess: () => {
                close();
                navigate('/cloud-compute');
            },
        });
    };

    return (
        <div className="w-full rounded-md bg-surface-1 border-[0.5px] border-[#3f1d1d] light:border-red-300 overflow-hidden">
            <div className="px-4 py-3 border-b-[0.5px] border-[#3f1d1d] light:border-red-300 flex items-center gap-2">
                <IconAlertTriangle size={18} color="#ef4444" />
                <span className="text-base font-bold text-(--color-error)">
                    {t('dangerZone.title')}
                </span>
                <span className="text-sm text-fg/68">{t('dangerZone.subtitle')}</span>
            </div>
            <div className="p-4 flex flex-row items-start justify-between gap-4">
                <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-base text-fg">{t('dangerZone.destroyInstance')}</span>
                    <span className="text-sm text-fg/68">
                        {t('dangerZone.destroyInstanceDescription')}
                    </span>
                </div>
                <Button
                    variant="danger"
                    leftIcon={<IconTrash size={16} />}
                    onClick={open}
                    disabled={destroyMutation.isPending}
                >
                    {t('dangerZone.destroyButton')}
                </Button>
            </div>

            <ConfirmActionModal
                opened={opened}
                onClose={close}
                title={t('dangerZone.destroyConfirmTitle')}
                message={t('dangerZone.destroyConfirmMessage', { name: hostname })}
                confirmLabel={t('dangerZone.destroyConfirmLabel')}
                confirmVariant="danger"
                isLoading={destroyMutation.isPending}
                onConfirm={confirmDestroy}
            />
        </div>
    );
}
